import { z } from "zod";
import { mediaUrlSchema } from "./media";

/**
 * A picture behind the stage: behind every slide of a deck (its theme), or behind one
 * slide in particular.
 *
 * Text on a photograph is the classic way to lose a room, so the picture is never shown
 * bare: the theme's background colour is laid over it, strongly enough that the stage's
 * text — whose colour still comes from that background (themeColours) — keeps its
 * contrast on the picture's brightest (or, on a light theme, darkest) parts. How strong
 * that is follows from two numbers measured as the picture is uploaded; a presenter
 * who wants more of the picture, or less, can set it themselves.
 */
export const backdropSchema = z
  .object({
    /** An image uploaded to the organisation (media.ts). */
    src: mediaUrlSchema,
    /**
     * Where its subject is, from 0 to 1 across and down: what stays in frame when a
     * screen of another shape — a projector, a phone — crops it to fill.
     */
    focusX: z.number().min(0).max(1).default(0.5),
    focusY: z.number().min(0).max(1).default(0.5),
    /** The luminance of its darker and lighter parts (10th and 90th percentiles); null if unmeasured. */
    dark: z.number().min(0).max(1).nullable().default(null),
    light: z.number().min(0).max(1).nullable().default(null),
    /** How much of the background colour is laid over it, 0 to 0.9; null to decide from the picture. */
    shade: z.number().min(0).max(0.9).nullable().default(null),
  })
  .strict();

export type Backdrop = z.infer<typeof backdropSchema>;

/** The least contrast the stage's text keeps against the shaded picture. */
const TARGET = 5;
/** Always some of the theme's colour, so the picture sits in the look; never all of it. */
const [LEAST, MOST] = [0.25, 0.9];

const lin = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};
function luminanceOf(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}
/** A luminance as the 0–1 value a browser blends — colours mix in sRGB, not in light. */
const encoded = (l: number) => Math.max(0, l) ** (1 / 2.2);

/**
 * How strongly to shade a picture with the stage's background colour: the presenter's
 * choice if they made one, or else the least that keeps the text readable.
 */
export function backdropShade(image: Pick<Backdrop, "dark" | "light" | "shade">, background: string): number {
  if (image.shade !== null) return image.shade;
  if (image.dark === null || image.light === null) return 0.6;

  const bg = encoded(luminanceOf(background));
  // A light theme sets dark text (themeColours), which needs the picture's dark parts
  // lifted; a dark one sets white text, which needs its light parts brought down.
  const lightTheme = luminanceOf(background) > 0.45;
  let needed: number;
  if (lightTheme) {
    const ink = luminanceOf("#17142a");
    const floor = encoded(TARGET * (ink + 0.05) - 0.05);
    const worst = encoded(image.dark);
    needed = worst >= floor ? 0 : (floor - worst) / Math.max(1e-6, bg - worst);
  } else {
    const ceiling = encoded(1.05 / TARGET - 0.05);
    const worst = encoded(image.light);
    needed = worst <= ceiling ? 0 : (worst - ceiling) / Math.max(1e-6, worst - bg);
  }
  return Math.round(Math.min(MOST, Math.max(LEAST, needed)) * 100) / 100;
}

/** The picture as CSS layers: the shade over the photo, cropped around its subject. */
export function backdropLayers(image: Backdrop, background: string, src = image.src): { backgroundImage: string; backgroundPosition: string } {
  const alpha = Math.round(backdropShade(image, background) * 255)
    .toString(16)
    .padStart(2, "0");
  const shade = `${background}${alpha}`;
  return {
    backgroundImage: `linear-gradient(${shade}, ${shade}), url("${src}")`,
    backgroundPosition: `center, ${Math.round(image.focusX * 100)}% ${Math.round(image.focusY * 100)}%`,
  };
}
